import { AlgoliaProductItem } from "../apiUtils/algoliaUtils/types";
import {
  AppliedPromotionsType,
  AttributesTypes,
  AvailablePromotion,
  Checkout,
  CheckoutLines,
  MediaItem,
  Order,
  Product,
  ProductAttributeOverride,
  TranslationData,
  VariantItem,
  VariantItemPdp,
  WishlistItem,
  WishlistResponse,
} from "../common/lib/types";
import calculateProductBadges from "./badgeUtils";
import {
  getValueBasedOnLanguage,
  hasArrayElements,
  isFallBackTranslationNeeded,
  isTranslationNeeded,
  mapEdgesToItems,
  parseNumberFromAttributeValue,
  toPrecise,
} from "./commonUtils";
import {
  AttributeInputTypeEnum,
  attributesWithUnits,
  ProductType,
  rewardValueTypes,
  SaleorAttributes,
  saleorMediaType,
  videoExtensions,
} from "./constants";

const brandId: string = process.env.NEXT_PUBLIC_BRAND_ID || "";

export const groupByAttributes = (
  variants: VariantItemPdp[],
  attributes: string[],
  language: string,
) => {
  return variants?.reduce(
    (acc, variant) => {
      const key = attributes
        .map((attr) => getAttributeValue(variant?.attributes, attr, null, language))
        .filter(Boolean)
        .join("-");
      if (key) {
        if (!acc[key]) acc[key] = [];
        acc[key].push(variant);
      }
      return acc;
    },
    {} as Record<string, VariantItemPdp[]>,
  );
};

/**
 * Sorts an array of variants by price and quantity.
 * @param variants - The array of VariantItemPdp objects to be sorted.
 * @returns The sorted array of VariantItemPdp objects.
 */
export const getSortedVariants = (variants: VariantItemPdp[]): VariantItemPdp[] => {
  return [...variants].sort((a, b) => {
    const priceA = a?.pricing?.price?.gross?.amount ?? 0;
    const priceB = b?.pricing?.price?.gross?.amount ?? 0;

    // Sort out-of-stock products last
    if (a.quantityAvailable === 0 && b.quantityAvailable > 0) return 1;
    if (b.quantityAvailable === 0 && a.quantityAvailable > 0) return -1;

    // Sort by price for in-stock or both out-of-stock
    if (priceA !== priceB) return priceA - priceB;

    // If prices are equal, sort by quantity (descending)
    return b.quantityAvailable - a.quantityAvailable;
  });
};

export const sortLensPower = (powers: string[]): string[] => {
  return hasArrayElements(powers) ? powers.sort((a, b) => parseFloat(a) - parseFloat(b)) : [];
};

const transformAttributeValueWithUnit = (
  attribute: string,
  value: string,
  language: string,
): string | null => {
  if (!value) return null;

  const formatNumberValue = (numString: string, unit?: string) => {
    const parsedValue = parseFloat(numString);
    if (isNaN(parsedValue)) return null;
    const formattedValue = Number.isInteger(parsedValue) ? parsedValue : parsedValue.toFixed(2);
    return `${formattedValue} ${unit ?? ""}`;
  };

  switch (attribute) {
    case SaleorAttributes.CL_POWER:
    case SaleorAttributes.ACC_POWER: {
      const [power, sign] = value.split(" ");
      const parsedPower = parseFloat(power);

      if (isNaN(parsedPower)) return null;
      if (parsedPower === 0) return "0";

      const formattedPower = parsedPower.toFixed(2);
      if (sign === "negative") {
        return `-${formattedPower}`;
      } else if (sign === "positive") {
        return `+${formattedPower}`;
      }
      return formattedPower;
    }
    case SaleorAttributes.CL_WATER_CONTENT: {
      const formattedValue = formatNumberValue(value);
      return `${formattedValue} %`;
    }
    case SaleorAttributes.ACC_VOLUME: {
      const [numberValue, unit] = value.split(" ");
      return formatNumberValue(
        numberValue,
        unit?.replace(/MILLILITER/g, language === "ar" ? "مل" : "ml"),
      );
    }
    case SaleorAttributes.CL_PER_PACK_SIZE: {
      const [numberValue] = value.split(" ");
      const formattedValue = formatNumberValue(numberValue);
      if (!formattedValue) return null;
      const unit = language === "ar" ? "عبوة من" : "Pack of";
      return `${unit} ${formattedValue}`;
    }
    case SaleorAttributes.LENS_SIZE:
    case SaleorAttributes.BRIDGE_SIZE:
    case SaleorAttributes.FRAME_LENGTH:
    case SaleorAttributes.FRAME_SHAPE:
    case SaleorAttributes.TEMPLE_SIZE:
    case SaleorAttributes.CL_BASE_CURVE:
    case SaleorAttributes.CL_DIAMETER: {
      const [numberValue, unit] = value.split(" ");
      return formatNumberValue(
        numberValue,
        unit?.replace(/MILLIMETER/g, language === "ar" ? "ملم" : "mm"),
      );
    }
    default: {
      const [numberValue, unit] = value.split(" ");
      return formatNumberValue(numberValue, unit);
    }
  }
};

const getNestedValue = (obj: any, path: string[]): string => {
  return path.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj);
};

export const getParsedJson = (property: string | null | undefined) =>
  property ? JSON.parse(property) : "";

export const getNestedValueByString = (
  property: string | null | undefined,
  propertyPath: string[],
): string => {
  const parsedJson = getParsedJson(property);
  return parsedJson ? getNestedValue(parsedJson, propertyPath) : "";
};

export const getParsedJsonValue = (
  attributeValueEn: string | null | undefined,
  attributeValueAr: string | null | undefined,
  language: string = "en",
): string => {
  const hasTranslationNeeded = isTranslationNeeded(language);
  if (hasTranslationNeeded && attributeValueAr) {
    const parsedJson = getParsedJson(attributeValueAr);
    if (parsedJson?.value) {
      return parsedJson?.value;
    }
  }
  return (
    (hasTranslationNeeded && !isFallBackTranslationNeeded()
      ? ""
      : getParsedJson(attributeValueEn)?.value) || ""
  );
};

export const getParsedNestedValue = (
  attributeValueEn: string | null | undefined,
  attributeValueAr: string | null | undefined,
  propertyPath: string[],
  language: string = "en",
): string => {
  const hasTranslationNeeded = isTranslationNeeded(language);
  if (hasTranslationNeeded && attributeValueAr) {
    const nestedValueAr = getNestedValueByString(attributeValueAr, propertyPath);
    if (nestedValueAr) {
      return nestedValueAr;
    }
  }
  return hasTranslationNeeded && !isFallBackTranslationNeeded()
    ? ""
    : getNestedValueByString(attributeValueEn, propertyPath) || "";
};

export function getSelectedAttributeValue(
  attribute: AttributesTypes,
  propertyPath: string[],
  language: string = "en",
): string | boolean | null {
  const attributeValue = attribute?.values?.[0];
  const { referenceEntity, scoppable, richText, boolean, translation } = attributeValue || {};

  let result: string | boolean | null = null;

  switch (attribute?.attribute?.inputType) {
    case AttributeInputTypeEnum.REFERENCE_ENTITY: {
      try {
        return getParsedNestedValue(
          referenceEntity,
          translation?.referenceEntity,
          propertyPath,
          language,
        );
      } catch {
        return null;
      }
    }
    case AttributeInputTypeEnum.SCOPABLE_REFERENCE_ENTITY: {
      try {
        return getParsedNestedValue(scoppable, translation?.scoppable, [brandId], language);
      } catch {
        return null;
      }
    }
    case AttributeInputTypeEnum.PLAIN_TEXT:
      result = getValueBasedOnLanguage(
        language,
        attributeValue,
        "plainText",
        "plainText",
        translation,
      );
      break;
    case AttributeInputTypeEnum.RICH_TEXT:
      try {
        return getParsedJsonValue(richText, translation?.richText, language);
      } catch {
        return null;
      }
    case AttributeInputTypeEnum.DROPDOWN:
      result = getValueBasedOnLanguage(language, attributeValue, "name", "name", translation);
      result = result === "-" ? null : result;
      break;
    case AttributeInputTypeEnum.MULTISELECT: {
      if (!attribute?.values?.length) return null;
      const values = attribute.values
        .map((v) =>
          getValueBasedOnLanguage(
            language,
            v,
            "name",
            "name",
            (v as { translation?: { name?: string } })?.translation,
          ),
        )
        .filter((s): s is string => typeof s === "string" && s.trim() !== "" && s !== "-");
      return values.length > 0 ? values.join(", ") : null;
    }
    case AttributeInputTypeEnum.BOOLEAN:
      return boolean;
    default:
      result = getValueBasedOnLanguage(language, attributeValue, "name", "name", translation);
  }

  if (
    typeof result === "string" &&
    attributesWithUnits.includes(attribute?.attribute?.slug as SaleorAttributes) &&
    result
  ) {
    return transformAttributeValueWithUnit(attribute?.attribute?.slug, result, language);
  }

  return result;
}

const getAttributeLabel = (attribute: AttributesTypes, language: string = "en"): string => {
  const { name, translation } = attribute?.attribute || {};
  const isTranslationRequired = isTranslationNeeded(language);
  return isTranslationRequired && translation?.name ? translation?.name : name;
};

export function getAttributeValue(
  attributes: AttributesTypes[] = [],
  attributeName: string = "",
  propertyPath?: string[] | null,
  language?: string,
): string | boolean | null {
  const attribute = attributes?.find((attr) => attr?.attribute?.slug === attributeName);
  const propertyPaths = propertyPath || ["code"];

  if (!attribute) return null;

  return getSelectedAttributeValue(attribute, propertyPaths, language);
}

export function getValueByAttribute(
  attribute: AttributesTypes,
  propertyPath?: string[] | null,
  language?: string,
): string | boolean | null {
  const propertyPaths = propertyPath || ["code"];

  if (!attribute) return null;

  return getSelectedAttributeValue(attribute, propertyPaths, language);
}

export function getAttributeValueAndLabel(
  attributes: AttributesTypes[] = [],
  attributeName: string = "",
  propertyPath?: string[] | null,
  language?: string,
): { label: string; value: string | boolean | null } {
  const attribute = attributes?.find((attr) => attr?.attribute?.slug === attributeName);
  const propertyPaths = propertyPath || ["code"];

  if (!attribute) return { label: "", value: null };

  return {
    label: getAttributeLabel(attribute, language),
    value: getSelectedAttributeValue(attribute, propertyPaths, language) ?? null,
  };
}

export const getProductHighlights = (
  productAttributes: AttributesTypes[],
  variantAttributes: AttributesTypes[],
  productType?: string,
  language?: string,
): { name: string; value: string | boolean; code: string }[] => {
  const getVariantAttributeValue = (attribute: string, propertyPath?: string[]) =>
    getAttributeValue(variantAttributes, attribute, propertyPath, language) ?? "";

  const getProductAttributeValue = (attribute: string, propertyPath?: string[]) =>
    getAttributeValue(productAttributes, attribute, propertyPath, language) ?? "";

  switch (productType) {
    case ProductType.SUNGLASSES:
    case ProductType.EYEGLASSES: {
      const frameMaterial = getProductAttributeValue(SaleorAttributes.FRAME_MATERIAL, [
        "attributes",
        "label",
      ]);
      const shape = getProductAttributeValue(SaleorAttributes.SHAPE, ["attributes", "label"]);
      return [
        { name: frameMaterial as string, value: frameMaterial, code: "fm" },
        { name: shape as string, value: shape, code: "s" },
      ];
    }
    case ProductType.ACC_BY_COLOR:
    case ProductType.ACC_BY_COLOR_BY_POWER: {
      const accColor = getVariantAttributeValue(SaleorAttributes.ACC_COLOR, [
        "attributes",
        "label",
      ]);
      const accGender = getVariantAttributeValue(SaleorAttributes.ACC_GENDER);
      return [
        { name: accColor as string, value: accColor, code: "ac" },
        { name: accGender as string, value: accGender, code: "ag" },
      ];
    }
    case ProductType.ACC_VOLUME: {
      const accVolume = getVariantAttributeValue(SaleorAttributes.ACC_VOLUME) as string;
      return [{ name: accVolume, value: accVolume, code: "av" }];
    }
    case ProductType.CL_CLEAR:
    case ProductType.CL_COLOR: {
      const uvBlock = getVariantAttributeValue(SaleorAttributes.UV_BLOCK);
      const clDuration = getProductAttributeValue(SaleorAttributes.CL_DURATION);
      const clMaterial = getVariantAttributeValue(SaleorAttributes.CL_MATERIAL, [
        "attributes",
        "label",
      ]);
      return [
        { name: clDuration as string, value: clDuration, code: "cld" },
        { name: clMaterial as string, value: clMaterial, code: "clm" },
        { name: uvBlock ? "UV" : "", value: uvBlock, code: "uvb" },
      ];
    }
    default:
      return [];
  }
};

export const getSpecsForProductType = (
  productType: string,
  variantAttributes: AttributesTypes[],
  productAttributes: AttributesTypes[],
  translations?: Record<string, string>,
  language?: string,
  productId?: string,
  productAttributeOverrides?: ProductAttributeOverride[],
) => {
  const getVariantAttributeValue = (attribute: string, propertyPath?: string[]) =>
    (getAttributeValue(variantAttributes, attribute, propertyPath, language) as string) ?? "";
  const getProductAttributeValue = (attribute: string, propertyPath?: string[]) =>
    (getAttributeValue(productAttributes, attribute, propertyPath, language) as string) ?? "";
  const getVariantAttributeLabel = (attribute: string, propertyPath?: string[]) =>
    getAttributeValueAndLabel(variantAttributes, attribute, propertyPath, language) ?? "";
  const getProductAttributeLabel = (attribute: string, propertyPath?: string[]) =>
    getAttributeValueAndLabel(productAttributes, attribute, propertyPath, language) ?? "";

  const getClPerPackSizeOverride = (isPowered: boolean): number | null => {
    if (productId && productAttributeOverrides) {
      const override = productAttributeOverrides.find(
        (override) => override.productId === productId,
      );
      if (override) {
        const value = isPowered ? override.clPerPackSize : override.clPerPackSizeZeroPower;
        if (value !== undefined && value !== null) {
          return value;
        }
      }
    }
    return null;
  };

  switch (productType) {
    case ProductType.SUNGLASSES:
      return [
        {
          label: translations?.common_variant_attribute_frame_color_label,
          value: getVariantAttributeValue(SaleorAttributes.FRAME_COLOR, ["attributes", "label"]),
        },
        {
          label: translations?.common_variant_attribute_lens_color_label,
          value: getVariantAttributeValue(SaleorAttributes.LENS_COLOR, ["attributes", "label"]),
        },
        {
          label: translations?.common_variant_attribute_gender_label,
          value: getVariantAttributeValue(SaleorAttributes.GENDER),
        },
        {
          label: translations?.common_variant_attribute_fit_label,
          value: getVariantAttributeValue(SaleorAttributes.FRAME_FIT, ["attributes", "label"]),
        },
        {
          label: translations?.common_variant_attribute_model_label,
          value: getVariantAttributeValue(SaleorAttributes.MODEL),
        },
      ];
    case ProductType.EYEGLASSES:
      const parentBrand = getProductAttributeValue(SaleorAttributes.PARENT_BRAND);
      const model = getVariantAttributeLabel(SaleorAttributes.MODEL, ["attributes", "label"]);
      return [
        {
          label: translations?.common_variant_attribute_frame_color_label,
          value: getVariantAttributeValue(SaleorAttributes.FRAME_COLOR, ["attributes", "label"]),
        },
        {
          label: translations?.common_variant_attribute_gender_label,
          value: getVariantAttributeValue(SaleorAttributes.GENDER),
        },
        {
          label: translations?.common_variant_attribute_fit_label,
          value: getVariantAttributeValue(SaleorAttributes.FRAME_FIT, ["attributes", "label"]),
        },
        ...(parentBrand === "pair_eyewear"
          ? [
              {
                label: model.label,
                value: getVariantAttributeValue(SaleorAttributes.MODEL, ["attributes", "label"]),
              },
            ]
          : []),
      ];
    case ProductType.ACC_BY_COLOR:
      return [
        {
          label: translations?.common_variant_attribute_sub_category_label,
          value: getProductAttributeValue(SaleorAttributes.ACC_TYPE),
        },
        {
          label: translations?.common_variant_attribute_color_label,
          value: getVariantAttributeValue(SaleorAttributes.ACC_COLOR, ["attributes", "label"]),
        },
      ];
    case ProductType.ACC_BY_COLOR_BY_POWER:
      return [
        {
          label: translations?.common_variant_attribute_sub_category_label,
          value: getProductAttributeValue(SaleorAttributes.ACC_TYPE),
        },
        {
          label: translations?.common_variant_attribute_color_label,
          value: getVariantAttributeValue(SaleorAttributes.ACC_COLOR, ["attributes", "label"]),
        },
        {
          label: translations?.common_variant_attribute_power_label,
          value: getVariantAttributeValue(SaleorAttributes.ACC_POWER),
        },
      ];
    case ProductType.ACC_BY_FRAMECOLOR_BY_LENSCOLOR:
      const clipOnStyle = getProductAttributeLabel(SaleorAttributes.CLIPON_STYLE, [
        "attributes",
        "label",
      ]);
      const lensColor = getVariantAttributeLabel(SaleorAttributes.CLIPON_LENSCOLOR, [
        "attributes",
        "label",
      ]);
      const frameColor = getVariantAttributeLabel(SaleorAttributes.CLIPON_FRAMECOLOR, [
        "attributes",
        "label",
      ]);
      return [
        {
          label: clipOnStyle.label,
          value: getProductAttributeValue(SaleorAttributes.CLIPON_STYLE, ["attributes", "label"]),
        },
        {
          label: lensColor.label,
          value: getVariantAttributeValue(SaleorAttributes.CLIPON_LENSCOLOR, [
            "attributes",
            "label",
          ]),
        },
        {
          label: frameColor.label,
          value: getVariantAttributeValue(SaleorAttributes.CLIPON_FRAMECOLOR, [
            "attributes",
            "label",
          ]),
        },
      ];
    case ProductType.ACC_VOLUME:
      return [
        {
          label: translations?.common_variant_attribute_sub_category_label,
          value: getProductAttributeValue(SaleorAttributes.ACC_TYPE),
        },
        {
          label: translations?.common_variant_attribute_volume_label,
          value: getVariantAttributeValue(SaleorAttributes.ACC_VOLUME),
        },
      ];
    case ProductType.CL_CLEAR: {
      const clPower = getVariantAttributeValue(SaleorAttributes.CL_POWER);
      const isPowered = parseFloat(clPower) !== 0;
      const overrideValue = getClPerPackSizeOverride(isPowered);
      const noOfLensesPerBox =
        overrideValue !== null
          ? overrideValue
          : parseNumberFromAttributeValue(
              getVariantAttributeValue(SaleorAttributes.CL_PER_PACK_SIZE),
            );
      const specs = [
        {
          label: translations?.common_variant_attribute_power_label,
          value: clPower,
        },
      ];
      if (noOfLensesPerBox) {
        const lensesPerBoxLabel =
          noOfLensesPerBox === 1
            ? translations?.common_variant_attribute_no_of_lens_per_box_label
            : translations?.common_variant_attribute_no_of_lenses_per_box_label;
        specs.push({
          label: translations?.common_variant_attribute_box_label,
          value: `${noOfLensesPerBox} ${lensesPerBoxLabel ?? ""}`,
        });
      }
      return specs;
    }
    case ProductType.CL_COLOR: {
      const clPower = getVariantAttributeValue(SaleorAttributes.CL_POWER);
      const isPowered = parseFloat(clPower) !== 0;
      const overrideValue = getClPerPackSizeOverride(isPowered);
      const noOfLensesPerBox =
        overrideValue !== null
          ? overrideValue
          : parseNumberFromAttributeValue(
              getVariantAttributeValue(SaleorAttributes.CL_PER_PACK_SIZE),
            );
      const specs = [
        {
          label: translations?.common_variant_attribute_color_label,
          value: getVariantAttributeValue(SaleorAttributes.CL_COLOR, ["attributes", "label"]),
        },
        {
          label: translations?.common_variant_attribute_power_label,
          value: clPower,
        },
      ];
      if (noOfLensesPerBox) {
        const lensesPerBoxLabel =
          noOfLensesPerBox === 1
            ? translations?.common_variant_attribute_no_of_lens_per_box_label
            : translations?.common_variant_attribute_no_of_lenses_per_box_label;
        specs.push({
          label: translations?.common_variant_attribute_box_label,
          value: `${noOfLensesPerBox} ${lensesPerBoxLabel ?? ""}`,
        });
      }
      return specs;
    }
    default:
      return [];
  }
};

export const getProductInfo = (
  productType: string,
  language: string,
  productAttributes?: AttributesTypes[],
  variantAttributes?: AttributesTypes[],
  translations?: Record<string, string>,
) => {
  const getVariantAttributeValue = (attribute: string, propertyPath?: string[]) =>
    getAttributeValueAndLabel(variantAttributes, attribute, propertyPath, language);
  const getProductAttributeValue = (attribute: string, propertyPath?: string[]) =>
    getAttributeValueAndLabel(productAttributes, attribute, propertyPath, language);
  const getMeasurements = () => {
    const sizes = [
      getAttributeValue(variantAttributes, SaleorAttributes.LENS_SIZE, null, language),
      getAttributeValue(variantAttributes, SaleorAttributes.BRIDGE_SIZE, null, language),
      getAttributeValue(variantAttributes, SaleorAttributes.TEMPLE_SIZE, null, language),
    ];
    return sizes?.filter(Boolean)?.join(" | ");
  };
  switch (productType) {
    case ProductType.SUNGLASSES:
      return [
        {
          label: translations?.pdp_attribute_brand,
          value: getAttributeValue(
            productAttributes,
            SaleorAttributes.PARENT_BRAND,
            ["attributes", "label"],
            language,
          ),
        },
        getVariantAttributeValue(SaleorAttributes.MODEL),
        getVariantAttributeValue(SaleorAttributes.COLOR_CODE),
        getVariantAttributeValue(SaleorAttributes.EAN),
        getProductAttributeValue(SaleorAttributes.SHAPE, ["attributes", "label"]),
        getProductAttributeValue(SaleorAttributes.FRAME_MATERIAL, ["attributes", "label"]),
        getVariantAttributeValue(SaleorAttributes.FRAME_COLOR, ["attributes", "label"]),
        getVariantAttributeValue(SaleorAttributes.LENS_MATERIAL, ["attributes", "label"]),
        getVariantAttributeValue(SaleorAttributes.LENS_TECHNOLOGY),
        getVariantAttributeValue(SaleorAttributes.LENS_COLOR, ["attributes", "label"]),
        { label: translations?.pdp_attribute_measurements, value: getMeasurements() },
        {
          label: getVariantAttributeValue(SaleorAttributes.POLARIZED)?.label,
          value: getVariantAttributeValue(SaleorAttributes.POLARIZED)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        {
          label: getVariantAttributeValue(SaleorAttributes.MIRRORED)?.label,
          value: getVariantAttributeValue(SaleorAttributes.MIRRORED)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        getVariantAttributeValue(SaleorAttributes.GEO_FIT, ["attributes", "label"]),
        {
          label: getVariantAttributeValue(SaleorAttributes.PHOTOCHROMIC)?.label,
          value: getVariantAttributeValue(SaleorAttributes.PHOTOCHROMIC)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        {
          label: getVariantAttributeValue(SaleorAttributes.GRADIENT)?.label,
          value: getVariantAttributeValue(SaleorAttributes.GRADIENT)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        {
          label: getVariantAttributeValue(SaleorAttributes.ANTI_REFLECTIVE)?.label,
          value: getVariantAttributeValue(SaleorAttributes.ANTI_REFLECTIVE)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        {
          label: getVariantAttributeValue(SaleorAttributes.PRINTED)?.label,
          value: getVariantAttributeValue(SaleorAttributes.PRINTED)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        {
          label: getVariantAttributeValue(SaleorAttributes.BLUE_LIGHT_CUT)?.label,
          value: getVariantAttributeValue(SaleorAttributes.BLUE_LIGHT_CUT)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        {
          label: getVariantAttributeValue(SaleorAttributes.SOLID_COLOR)?.label,
          value: getVariantAttributeValue(SaleorAttributes.SOLID_COLOR)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        getVariantAttributeValue(SaleorAttributes.MATERIAL_COMPOSITION, ["attributes", "label"]),
        getVariantAttributeValue(SaleorAttributes.FRAME_FINISH, ["attributes", "label"]),
      ];
    case ProductType.EYEGLASSES:
      return [
        {
          label: translations?.pdp_attribute_brand,
          value: getAttributeValue(
            productAttributes,
            SaleorAttributes.PARENT_BRAND,
            ["attributes", "label"],
            language,
          ),
        },
        getVariantAttributeValue(SaleorAttributes.MODEL),
        getVariantAttributeValue(SaleorAttributes.COLOR_CODE),
        getVariantAttributeValue(SaleorAttributes.EAN),
        getProductAttributeValue(SaleorAttributes.SHAPE, ["attributes", "label"]),
        getProductAttributeValue(SaleorAttributes.FRAME_MATERIAL, ["attributes", "label"]),
        getVariantAttributeValue(SaleorAttributes.FRAME_COLOR, ["attributes", "label"]),
        getVariantAttributeValue(SaleorAttributes.LENS_MATERIAL, ["attributes", "label"]),
        getVariantAttributeValue(SaleorAttributes.LENS_TECHNOLOGY),
        getVariantAttributeValue(SaleorAttributes.LENS_COLOR, ["attributes", "label"]),
        { label: translations?.pdp_attribute_measurements, value: getMeasurements() },
        getVariantAttributeValue(SaleorAttributes.GEO_FIT, ["attributes", "label"]),
        {
          label: getVariantAttributeValue(SaleorAttributes.PHOTOCHROMIC)?.label,
          value: getVariantAttributeValue(SaleorAttributes.PHOTOCHROMIC)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        {
          label: getVariantAttributeValue(SaleorAttributes.GRADIENT)?.label,
          value: getVariantAttributeValue(SaleorAttributes.GRADIENT)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        {
          label: getVariantAttributeValue(SaleorAttributes.ANTI_REFLECTIVE)?.label,
          value: getVariantAttributeValue(SaleorAttributes.ANTI_REFLECTIVE)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        {
          label: getVariantAttributeValue(SaleorAttributes.PRINTED)?.label,
          value: getVariantAttributeValue(SaleorAttributes.PRINTED)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        {
          label: getVariantAttributeValue(SaleorAttributes.BLUE_LIGHT_CUT)?.label,
          value: getVariantAttributeValue(SaleorAttributes.BLUE_LIGHT_CUT)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        {
          label: getVariantAttributeValue(SaleorAttributes.SOLID_COLOR)?.label,
          value: getVariantAttributeValue(SaleorAttributes.SOLID_COLOR)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        getVariantAttributeValue(SaleorAttributes.MATERIAL_COMPOSITION, ["attributes", "label"]),
        getVariantAttributeValue(SaleorAttributes.FRAME_FINISH, ["attributes", "label"]),
      ];
    case ProductType.ACC_VOLUME:
      return [
        {
          label: translations?.pdp_attribute_brand,
          value: getAttributeValue(
            productAttributes,
            SaleorAttributes.PARENT_BRAND,
            ["attributes", "label"],
            language,
          ),
        },
        getVariantAttributeValue(SaleorAttributes.EAN),
        getProductAttributeValue(SaleorAttributes.ACC_GENDER),
        getProductAttributeValue(SaleorAttributes.ACC_TYPE),
        getVariantAttributeValue(SaleorAttributes.ACC_VOLUME),
      ];
    case ProductType.ACC_BY_COLOR:
    case ProductType.ACC_BY_COLOR_BY_POWER:
      return [
        {
          label: translations?.pdp_attribute_brand,
          value: getAttributeValue(
            productAttributes,
            SaleorAttributes.PARENT_BRAND,
            ["attributes", "label"],
            language,
          ),
        },
        getVariantAttributeValue(SaleorAttributes.EAN),
        getVariantAttributeValue(SaleorAttributes.ACC_MODEL),
        getVariantAttributeValue(SaleorAttributes.ACC_COLOR, ["attributes", "label"]),
        getVariantAttributeValue(SaleorAttributes.ACC_GENDER),
        getProductAttributeValue(SaleorAttributes.ACC_TYPE),
        getProductAttributeValue(SaleorAttributes.ACC_MATERIAL, ["attributes", "label"]),
      ];
    case ProductType.CL_CLEAR: {
      return [
        {
          label: translations?.pdp_attribute_brand,
          value: getAttributeValue(
            productAttributes,
            SaleorAttributes.PARENT_BRAND,
            ["attributes", "label"],
            language,
          ),
        },
        getVariantAttributeValue(SaleorAttributes.CL_MATERIAL, ["attributes", "label"]),
        getVariantAttributeValue(SaleorAttributes.LENS_TECHNOLOGY),
        getVariantAttributeValue(SaleorAttributes.CL_COLOR, ["attributes", "label"]),
        getProductAttributeValue(SaleorAttributes.CL_WATER_CONTENT),
        getVariantAttributeValue(SaleorAttributes.CL_DIAMETER),
        getVariantAttributeValue(SaleorAttributes.CL_BASE_CURVE),
        {
          label: getVariantAttributeValue(SaleorAttributes.UV_BLOCK)?.label,
          value: getVariantAttributeValue(SaleorAttributes.UV_BLOCK)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        getVariantAttributeValue(SaleorAttributes.CL_DISPOSABILITY),
      ];
    }
    case ProductType.CL_COLOR:
      return [
        {
          label: translations?.pdp_attribute_brand,
          value: getAttributeValue(
            productAttributes,
            SaleorAttributes.PARENT_BRAND,
            ["attributes", "label"],
            language,
          ),
        },
        getVariantAttributeValue(SaleorAttributes.CL_MATERIAL, ["attributes", "label"]),
        getVariantAttributeValue(SaleorAttributes.LENS_TECHNOLOGY),
        getVariantAttributeValue(SaleorAttributes.CL_COLOR, ["attributes", "label"]),
        getProductAttributeValue(SaleorAttributes.CL_WATER_CONTENT),
        getVariantAttributeValue(SaleorAttributes.CL_DIAMETER),
        getVariantAttributeValue(SaleorAttributes.CL_BASE_CURVE),
        {
          label: getVariantAttributeValue(SaleorAttributes.UV_BLOCK)?.label,
          value: getVariantAttributeValue(SaleorAttributes.UV_BLOCK)?.value
            ? translations?.pdp_attribute_value_yes
            : translations?.pdp_attribute_value_no,
        },
        getVariantAttributeValue(SaleorAttributes.CL_DISPOSABILITY),
        getVariantAttributeValue(SaleorAttributes.DISCLAIMER),
      ];
    default:
      return [];
  }
};

export const getProductImages = (
  attributes?: AttributesTypes[],
  productType?: string,
  language: string = "en",
): string[] => {
  const imageAttributeName =
    productType === ProductType.CL_CLEAR || productType === ProductType.CL_COLOR
      ? SaleorAttributes.CL_PRODUCT_IMAGE
      : SaleorAttributes.PRODUCT_IMAGE;
  const productImages = getAttributeValue(attributes, imageAttributeName, null, language) as
    | string
    | undefined;
  if (!productImages) {
    return [];
  }
  return productImages
    .split(",")
    .filter(Boolean)
    .sort((a, b) => getImageIndex(a) - getImageIndex(b));
};

export const getModelImages = (
  attributes?: AttributesTypes[],
  language: string = "en",
): string[] => {
  const modelImagesValue = getAttributeValue(
    attributes,
    SaleorAttributes.MODEL_IMAGE,
    null,
    language,
  ) as string | undefined;
  if (!modelImagesValue) {
    return [];
  }
  return modelImagesValue
    .split(",")
    .filter(Boolean)
    .sort((a, b) => getImageIndex(a) - getImageIndex(b));
};

export const getImageIndex = (str: string): number => {
  if (!str) {
    return Number.MAX_SAFE_INTEGER;
  }
  const urlWithoutQuery = str.split("?")[0] || "";
  const fileName = urlWithoutQuery.split("/").pop() || "";
  const match = fileName.match(/_(\d+)\.[^.]+$/);
  if (match) {
    const idx = parseInt(match[1], 10);
    return Number.isNaN(idx) ? Number.MAX_SAFE_INTEGER : idx;
  }
  return 0;
};

export const getVariantImages = (
  attributes?: AttributesTypes[],
  language?: string,
): MediaItem[] => {
  const getImagesArray = (attribute: string | undefined) =>
    attribute?.split(",").filter(Boolean) || [];

  const productImagesAttr = getAttributeValue(
    attributes,
    SaleorAttributes.PRODUCT_IMAGE,
    null,
    language,
  );
  const modelImagesAttr = getAttributeValue(
    attributes,
    SaleorAttributes.MODEL_IMAGE,
    null,
    language,
  );
  const clProductImageAttr = getAttributeValue(
    attributes,
    SaleorAttributes.CL_PRODUCT_IMAGE,
    null,
    language,
  );

  const productImages =
    typeof productImagesAttr === "string" ? getImagesArray(productImagesAttr) : [];
  const modelImages = typeof modelImagesAttr === "string" ? getImagesArray(modelImagesAttr) : [];
  const clProductImages =
    typeof clProductImageAttr === "string" ? getImagesArray(clProductImageAttr) : [];

  const sortedProductImages = [...productImages].sort((a, b) => getImageIndex(a) - getImageIndex(b));
  const sortedModelImages = [...modelImages].sort((a, b) => getImageIndex(a) - getImageIndex(b));
  const sortedClProductImages = [...clProductImages].sort((a, b) => getImageIndex(a) - getImageIndex(b));

  // Model stays in slot 1 to match the PDP gallery in PdpLayoutDetails.
  const firstProduct = sortedProductImages.shift() || sortedClProductImages.shift() || "";
  const secondSlot =
    sortedModelImages.shift() || sortedProductImages.shift() || sortedClProductImages.shift() || "";

  const media = Array.from(
    new Set(
      [
        firstProduct,
        secondSlot,
        ...sortedProductImages,
        ...sortedClProductImages,
        ...sortedModelImages,
      ].filter(Boolean),
    ),
  );

  return media.map((image) => ({ url: image, type: saleorMediaType.MEDIA_TYPE_IMAGE }));
};

export const getSortedVariantImages = (mediaItems: MediaItem[]): MediaItem[] => {
  if (hasArrayElements(mediaItems)) {
    const getImagesArrayByType = (type: string): string[] =>
      mediaItems
        ?.filter((media) => {
          if (!media?.url) return false;
          if (type === SaleorAttributes.PRODUCT_IMAGE) {
            return (
              media.url.includes(SaleorAttributes.PRODUCT_IMAGE) &&
              !media.url.includes(SaleorAttributes.CL_PRODUCT_IMAGE)
            );
          }
          return media.url.includes(type);
        })
        .map((m) => m?.url)
        .filter((url): url is string => Boolean(url));

    const getSortedImages: (images: string[]) => string[] = (images) =>
      [...images].sort((a, b) => getImageIndex(a) - getImageIndex(b));

    const productImages = getImagesArrayByType(SaleorAttributes.PRODUCT_IMAGE) || [];
    const modelImages = getImagesArrayByType(SaleorAttributes.MODEL_IMAGE) || [];
    const clProductImages = getImagesArrayByType(SaleorAttributes.CL_PRODUCT_IMAGE) || [];

    const sortedProductImages = getSortedImages(productImages);
    const sortedModelImages = getSortedImages(modelImages);
    const sortedClProductImages = getSortedImages(clProductImages);

    const firstProduct = sortedProductImages.shift() || sortedClProductImages.shift() || "";
    const secondSlot =
      sortedModelImages.shift() ||
      sortedProductImages.shift() ||
      sortedClProductImages.shift() ||
      "";

    const media = Array.from(
      new Set(
        [
          firstProduct,
          secondSlot,
          ...sortedProductImages,
          ...sortedClProductImages,
          ...sortedModelImages,
        ].filter(Boolean),
      ),
    );

    return media.map((image) => ({ url: image, type: saleorMediaType.MEDIA_TYPE_IMAGE }));
  }
  return [];
};

export type PricingWithDiscountPercent = {
  discountPercent?: number | null;
  discount?: { gross?: number | { amount?: number | null } | null } | null;
};

/** Display % from Saleor only. FIXED → null (caller shows amount off). */
export const getDisplayDiscountPercent = (
  pricing?: PricingWithDiscountPercent | null,
  promotion?: { rewardValueType?: string } | null,
): number | null => {
  if (`${promotion?.rewardValueType ?? ""}`.toUpperCase() === rewardValueTypes.FIXED) {
    return null;
  }
  return pricing?.discountPercent ?? null;
};

/** Saleor `pricing.discount` amount. No list−sale math. */
export const getDisplayDiscountAmount = (
  pricing?: PricingWithDiscountPercent | null,
): number | null => {
  const gross = pricing?.discount?.gross;
  const amount = typeof gross === "number" ? gross : gross?.amount;
  if (amount == null || Number.isNaN(Number(amount))) return null;
  return toPrecise(Math.abs(Number(amount)));
};

export const formatDiscountPercentLabel = (percent: number | null | undefined): string => {
  if (percent == null) return "";
  return `-${Math.abs(percent).toFixed(2)}%`;
};

export const getCalculatedPriceInfo = (
  undiscountedPrice: number,
  discountPrice: number,
  promotionDetails: AvailablePromotion | AppliedPromotionsType,
) => {
  let discountedPriceIn = discountPrice || 0;
  const isFlatOff = promotionDetails?.rewardValueType === rewardValueTypes.FIXED;
  discountedPriceIn = discountedPriceIn > 0 ? discountedPriceIn : 0;
  const percent = ((discountedPriceIn - undiscountedPrice) / Math.abs(undiscountedPrice)) * 100;
  return {
    discountValue: isFlatOff ? (discountedPriceIn - undiscountedPrice)?.toFixed(2) : 0,
    percent: percent,
    hasDiscount: discountedPriceIn < undiscountedPrice,
    undiscountedPrice: undiscountedPrice,
    discountedPrice: discountedPriceIn,
  };
};

export const buildProductPriceInfo = (
  undiscountedPrice: number,
  salePrice: number,
  promotionDetails?: AvailablePromotion | AppliedPromotionsType | null,
  pricing?: PricingWithDiscountPercent | null,
) => {
  const { hasDiscount, discountValue, undiscountedPrice: originalPrice, discountedPrice } =
    getCalculatedPriceInfo(undiscountedPrice, salePrice, promotionDetails || {});
  const displayPercent = getDisplayDiscountPercent(pricing, promotionDetails);

  return {
    originalPrice,
    price: discountedPrice,
    hasDiscount,
    discountValue: hasDiscount && discountValue ? discountValue : undefined,
    discountPercent: hasDiscount && !discountValue ? (displayPercent ?? undefined) : undefined,
  };
};

export const getPriceForBuyXgetY = (
  priceUndiscounted: number = 0,
  totalPrice: number = 0,
  quantity: number = 1,
) => {
  const discountedPriceIn: number = toPrecise(totalPrice / quantity, 2);
  const percent = ((discountedPriceIn - priceUndiscounted) / Math.abs(priceUndiscounted)) * 100;
  return {
    percent: toPrecise(percent),
    hasDiscount: discountedPriceIn < priceUndiscounted,
    discountedPrice: discountedPriceIn,
  };
};

export const filterAttributes = (
  attributes: AttributesTypes[] = [],
  saleorAttributes: string[],
): AttributesTypes[] => {
  if (hasArrayElements(attributes) && hasArrayElements(saleorAttributes)) {
    const res = attributes?.filter((attributeObj) =>
      saleorAttributes.includes(attributeObj?.attribute?.slug),
    );
    return res;
  }
  return attributes;
};

export const filterProductAttributes = (productObj: Product): Product => {
  const saleorAttributes: string[] = Object.values(SaleorAttributes);
  if (hasArrayElements(productObj?.attributes)) {
    productObj.attributes = filterAttributes(productObj?.attributes, saleorAttributes);
  }
  if (hasArrayElements(productObj?.variants!)) {
    productObj.variants = productObj?.variants?.map((variant) => ({
      ...variant,
      attributes: filterAttributes(variant?.attributes, saleorAttributes),
    }));
  }
  return productObj;
};

export const filterAttributesForOrder = (
  checkout?: Checkout | Order,
): Checkout | Order | undefined => {
  const saleorAttributes: string[] = Object.values(SaleorAttributes);
  if (
    checkout &&
    "availableGifts" in checkout &&
    checkout?.availableGifts &&
    hasArrayElements(checkout?.availableGifts)
  ) {
    checkout.availableGifts = checkout?.availableGifts?.map((gift) => ({
      ...gift,
      product: gift?.product && {
        ...gift?.product,
        attributes: filterAttributes(gift?.product?.attributes, saleorAttributes),
      },
    }));
  }
  if (checkout && hasArrayElements(checkout?.lines)) {
    checkout.lines = checkout?.lines?.map((line: CheckoutLines) => ({
      ...line,
      variant: line?.variant && {
        ...line?.variant,
        attributes: filterAttributes(line?.variant?.attributes, saleorAttributes),
        product: line?.variant?.product && {
          ...line?.variant?.product,
          attributes: filterAttributes(line?.variant?.product?.attributes, saleorAttributes),
        },
      },
    }));
  }
  return checkout;
};

export const sortProductVariants = (
  variants: Array<Record<string, any>>,
  defaultVariantId: string,
): Array<Record<string, any>> => {
  if (hasArrayElements(variants)) {
    return variants.sort((a, b) => {
      // If a is the defaultVariant and a is in stock, a comes first
      if (a?.variantId === defaultVariantId && a?.inStock) {
        return -1;
      }
      // If b is the defaultVariant and b is in stock, b comes first
      if (b?.variantId === defaultVariantId && b?.inStock) {
        return 1;
      }
      // If a is in stock and b is not, a comes before b
      if (a.inStock && !b.inStock) {
        return -1;
      }
      // If b is in stock and a is not, b comes before a
      if (!a.inStock && b.inStock) {
        return 1;
      }
      // Otherwise, maintain the order
      return 0;
    });
  }
  return [];
};

export const filterWishlistAttributes = (wishlistResponse: WishlistResponse): WishlistItem[] => {
  const wishlistItems = mapEdgesToItems(wishlistResponse?.items);
  const saleorAttributes: string[] = Object.values(SaleorAttributes);
  if (hasArrayElements(wishlistItems) && hasArrayElements(saleorAttributes)) {
    return wishlistItems?.map((wishlistItem: WishlistItem) => ({
      ...wishlistItem,
      variant: wishlistItem?.variant && {
        ...wishlistItem?.variant,
        attributes: filterAttributes(wishlistItem?.variant?.attributes, saleorAttributes),
        product: wishlistItem?.variant?.product && {
          ...wishlistItem?.variant?.product,
          attributes: filterAttributes(
            wishlistItem?.variant?.product?.attributes,
            saleorAttributes,
          ),
        },
      },
    }));
  }
  return [];
};

/**
 * This method will construct the media list and name for Algolia Product cards
 * Use base name and media for contact lens color products
 */
export const getProductNameAndMedia = (
  product: AlgoliaProductItem,
  selectedVariant: VariantItem,
  isDefaultVariant: boolean,
): { productMedia: MediaItem[]; productName: string } => {
  const { productType, groupedIndex, media, name } = product || {};
  
  // If selectedVariant has groupIndex === false, use base product media
  if (selectedVariant?.groupIndex === false) {
    return { productMedia: media || [], productName: name };
  }
  
  // For contact lens color products at the base level, use base product media
  if ([ProductType.CL_COLOR].includes(productType?.slug) && !groupedIndex && isDefaultVariant) {
    return { productMedia: media || [], productName: name };
  }
  
  // Default: use selectedVariant media
  return {
    productMedia: selectedVariant?.media || [],
    productName: selectedVariant?.variantName || name,
  };
};

export const getVariantColorCode = (variant: VariantItem): string => {
  return variant?.hexcode || "";
};

export const updateProductWithSelectedVariant = (
  product: Product,
  selectedVariant: VariantItemPdp | undefined,
  language: string,
  translations?: TranslationData,
  transactional?: boolean,
): Product => {
  const productWithBadgeDetails = calculateProductBadges(
    product,
    language,
    selectedVariant,
    translations,
    transactional,
  );
  const selectedVariantMedia = getVariantImages(selectedVariant?.attributes, language);
  const variantTitle = getAttributeValue(
    selectedVariant?.attributes,
    SaleorAttributes.TITLE,
    null,
    language,
  ) as string;
  const selectedVariantName = variantTitle
    ? variantTitle
    : getValueBasedOnLanguage(language, product, "name", "name", product?.translation);
  return { ...productWithBadgeDetails, selectedVariantMedia, selectedVariantName };
};
export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Helper function to extract file extension from URL
 * @param url - The media URL
 * @returns The file extension in lowercase or null if not found
 */
export const getFileExtension = (url: string): string | null => {
  if (!url) return null;

  // Remove query parameters
  const urlWithoutParams = url.split("?")[0];
  // Get the last part after the last dot
  const extension = urlWithoutParams.split(".").pop()?.toLowerCase();

  return extension || null;
};

/**
 * Helper function to check if media is a video based on file extension
 * This is needed because backend may incorrectly set type as IMAGE for MP4 files
 *
 * @param item - The media item to check
 * @returns true if the media is a video (either by type field or file extension)
 *
 * @example
 * const media = { url: "https://example.com/video.mp4", type: "IMAGE" };
 * isVideoMedia(media); // returns true (detected by .mp4 extension)
 */
export const isVideoMedia = (item: MediaItem | { url?: string; type?: string }): boolean => {
  if (!item?.url) return false;

  const fileExtension = getFileExtension(item.url);

  // Check both the type field AND file extension
  // This handles cases where backend incorrectly sets type as "IMAGE" for video files
  return (
    item?.type === saleorMediaType.MEDIA_TYPE_VIDEO ||
    (fileExtension !== null && videoExtensions.includes(fileExtension))
  );
};
